import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  IconButton,
  Switch,
  Tag,
} from '@ads/components-react';
import { Cog6ToothIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RevisionExamDetail, RevisionLang } from '@/features/exam-revision/types/revision';
import { Spinner } from '@/shared/ui/atoms';
import {
  LanguageForm,
  type LanguageFormPayload,
  type LanguageOption,
} from '@/shared/ui/organisms/LanguageForm';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';
import { getCultureTag } from '@/shared/utils/cultureTag';

type TagStatus = 'default' | 'highlight' | 'positive' | 'helper' | 'negative';

function statusVariant(statusId: number): TagStatus {
  if (statusId === 3) return 'positive';
  if (statusId === 2) return 'highlight';
  if (statusId === 1) return 'helper';
  return 'default';
}

function StatusTag({ statusId, statusText }: { statusId: number; statusText: string }): ReactNode {
  return (
    <Tag
      status={statusVariant(statusId)}
      size="md"
      custom={{ text: 'text-nowrap' }}
      label={statusText}
    />
  );
}

export interface RevisionLangsTemplateProps {
  readonly exam: RevisionExamDetail;
  readonly langs: RevisionLang[];
  readonly isLoading: boolean;
  /** Sinaliza falha ao carregar os idiomas. */
  readonly isError?: boolean;
  /** Recarrega a lista de idiomas após erro. */
  readonly onRetry?: () => void;
  /** Sinaliza que uma adição/edição está em andamento (bloqueia o formulário). */
  readonly isSaving?: boolean;
  readonly availableLangs: readonly LanguageOption[];
  readonly onSave: (
    payload: LanguageFormPayload,
    editingLangId: number | null,
    callbacks: { onSuccess: () => void },
  ) => void;
  readonly onTranslate: (lang: RevisionLang) => void;
  /** Ativa (`nextActive = true`) ou inativa (`false`) um idioma. */
  readonly onToggleActive: (lang: RevisionLang, nextActive: boolean) => void;
}

/**
 * Template da tela de idiomas de uma prova em revisão.
 *
 * Reproduz a aparência da tab de Idiomas da avaliação: cabeçalho com breadcrumb,
 * o {@link LanguageForm} compartilhado para adicionar/editar idioma inline e o
 * card "Idiomas adicionados" com a lista. A edição é inline — o lápis de cada
 * linha preenche o formulário acima. O status de revisão é exibido como tag de
 * workflow (Pendente/Revisado) e um `Switch` controla se o idioma está ativo.
 *
 * @param exam - Detalhes do exame (ID, título, disciplina, data)
 * @param langs - Lista de idiomas da prova
 * @param isLoading - Estado de carregamento
 * @param availableLangs - Idiomas selecionáveis ao adicionar
 * @param onSave - Callback ao salvar (payload + ID do idioma em edição, se houver)
 * @param onTranslate - Callback ao acionar as questões do idioma
 * @param onToggleActive - Callback ao ativar/inativar um idioma
 */
export function RevisionLangsTemplate({
  exam,
  langs,
  isLoading,
  isError = false,
  onRetry,
  isSaving = false,
  availableLangs,
  onSave,
  onTranslate,
  onToggleActive,
}: RevisionLangsTemplateProps) {
  const { t } = useTranslation('examRevision');
  const [editingLang, setEditingLang] = useState<RevisionLang | null>(null);
  const [formNonce, setFormNonce] = useState(0);

  const isEditMode = editingLang !== null;

  const labels = useMemo(
    () => ({
      header: isEditMode ? t('langs.form.editHeader') : t('langs.form.addHeader'),
      subtitle: t('langs.form.subtitle'),
      cultureInfo: t('langs.form.language'),
      cultureInfoPlaceholder: t('langs.form.languagePlaceholder'),
      title: t('langs.form.title'),
      titlePlaceholder: t('langs.form.titlePlaceholder'),
      instruction: t('langs.form.instruction'),
      instructionPlaceholder: t('langs.form.instructionPlaceholder'),
      instructionHint: t('langs.form.instructionHint'),
      active: t('langs.form.active'),
      save: isEditMode ? t('langs.form.save') : t('langs.form.create'),
      cancel: t('langs.form.cancel'),
    }),
    [t, isEditMode],
  );

  const formAvailableLangs = useMemo(
    () =>
      editingLang
        ? [
            {
              key: editingLang.cultureInfo,
              value: editingLang.cultureInfoDisplay,
            },
          ]
        : availableLangs,
    [editingLang, availableLangs],
  );

  function resetForm() {
    setEditingLang(null);
    setFormNonce(n => n + 1);
  }

  function handleFormSave(payload: LanguageFormPayload) {
    onSave(payload, editingLang?.id ?? null, { onSuccess: resetForm });
  }

  return (
    <div className="flex flex-col gap-mini">
      <PageHeader
        title={t('langs.title')}
        subtitle={t('langs.count', { title: exam.title, count: langs.length })}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">{t('breadcrumb.home')}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to={'/exams' as never}>{t('breadcrumb.exams')}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to={'/exam/revision' as never}>{t('breadcrumb.revision')}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to={`/exam/revision/${exam.id}/languages` as never}>{exam.title}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('breadcrumb.languages')}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      {/* Formulário de adicionar/editar idioma */}
      <LanguageForm
        key={`${editingLang ? `edit-${editingLang.id}` : 'add'}-${formNonce}`}
        availableLangs={formAvailableLangs}
        isEditMode={isEditMode}
        showActive={false}
        showTitle
        isSaving={isSaving}
        initialCultureInfo={editingLang?.cultureInfo ?? ''}
        initialTitle={editingLang?.title ?? ''}
        initialInstruction={editingLang?.instruction ?? ''}
        labels={labels}
        onSave={handleFormSave}
        onCancel={resetForm}
      />

      {!isEditMode && availableLangs.length === 0 && (
        <p className="ts-paragraph-xxs text-ctx-content-placeholder">{t('langs.form.allAdded')}</p>
      )}

      {/* Lista de idiomas adicionados */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        <div className="border-b-hairline border-ctx-layout-border px-sm py-mini">
          <h3 className="ts-subtitle-xs text-ctx-content-title">{t('langs.added.title')}</h3>
          <p className="ts-paragraph-xxs text-ctx-content-base mt-quark">
            {t('langs.added.subtitle', { count: langs.length })}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-xl">
            <Spinner size="md" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-nano py-xl">
            <span className="ts-paragraph-xs text-ctx-content-placeholder">{t('langs.error')}</span>
            {onRetry && (
              <Button hierarchy="secondary" size="sm" onClick={onRetry}>
                {t('langs.retry')}
              </Button>
            )}
          </div>
        ) : langs.length === 0 ? (
          <div className="flex items-center justify-center py-xl">
            <span className="ts-paragraph-xs text-ctx-content-placeholder">{t('langs.empty')}</span>
          </div>
        ) : (
          <div className="flex flex-col">
            {langs.map((lang, idx) => (
              <div
                key={lang.id}
                className={`flex items-center gap-xs px-sm py-xxxs ${idx > 0 ? 'border-t-hairline border-ctx-layout-border' : ''} ${editingLang?.id === lang.id ? 'bg-base-accent-50' : ''}`}
              >
                <div
                  className={`flex flex-1 items-center gap-xs ${lang.active ? '' : 'opacity-medium'}`}
                >
                  <Tag {...getCultureTag(lang.cultureInfo)} size="sm" />

                  <div className="flex flex-1 flex-col gap-quark">
                    <span className="ts-paragraph-xs text-ctx-content-title">
                      {lang.cultureInfoDisplay}
                    </span>
                    {lang.title && (
                      <span className="ts-paragraph-xxs text-ctx-content-base">{lang.title}</span>
                    )}
                  </div>

                  {!lang.active && (
                    <Tag
                      status="negative"
                      size="sm"
                      custom={{ text: 'text-nowrap' }}
                      label={t('langs.inactiveTag')}
                    />
                  )}

                  <StatusTag statusId={lang.statusId} statusText={lang.statusText} />
                </div>

                <IconButton
                  icon={PencilSquareIcon}
                  ariaLabel={t('langs.actions.questions')}
                  hierarchy="tertiary"
                  size="md"
                  onClick={() => onTranslate(lang)}
                />
                <IconButton
                  icon={Cog6ToothIcon}
                  ariaLabel={t('langs.actions.edit')}
                  hierarchy="tertiary"
                  size="md"
                  onClick={() => setEditingLang(lang)}
                />
                <Switch
                  checked={lang.active}
                  onCheckedChange={checked => onToggleActive(lang, Boolean(checked))}
                  size="md"
                  aria-label={
                    lang.active ? t('langs.actions.deactivate') : t('langs.actions.activate')
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
